import React, { useState, useCallback } from "react";
import { PlusIcon, TrashIcon } from "@aureskonnect/react-ui";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import ErrorMessage from "./ErrorMessage";
import type { Tranche } from "./shared";
export type { Tranche };

const MAX_AMOUNT = 99999.99;

function useDecimalInput(maxValue: number) {
    const [display, setDisplay] = useState("");
    const [numeric, setNumeric] = useState(0);

    const onChange = (raw: string) => {
        const filtered = raw.replace(/[^0-9.]/g, "");
        const deduped =
            filtered.split(".").length > 2
                ? filtered.slice(0, filtered.lastIndexOf("."))
                : filtered;
        const [intPart, decPart] = deduped.split(".");
        const val =
            decPart !== undefined
                ? `${intPart}.${decPart.slice(0, 2)}`
                : deduped;
        setDisplay(val);
        const num = parseFloat(val);
        setNumeric(val === "" || isNaN(num) ? 0 : Math.min(num, maxValue));
    };

    const onBlur = () => {
        setDisplay((prev) => {
            if (prev === "") return prev;
            const num = parseFloat(prev);
            if (isNaN(num)) return "";
            return Math.min(num, maxValue).toFixed(2);
        });
    };

    const reset = () => {
        setDisplay("");
        setNumeric(0);
    };

    return { display, numeric, onChange, onBlur, reset };
}

export const rebuildTranches = (list: Tranche[]): Tranche[] => {
    const normalized = list.map((tr) => ({
        ...tr,
        MinCommande: Number(tr.MinCommande) || 0,
        MaxCommande: Number(tr.MaxCommande) || 0,
        FraisLivraison: Number(tr.FraisLivraison) || 0,
    }));
    const sorted = normalized.sort((a, b) => a.MinCommande - b.MinCommande);
    return sorted.map((tr, idx) => ({
        ...tr,
        MaxCommande: idx < sorted.length - 1 ? sorted[idx + 1].MinCommande : 0,
    }));
};

interface TranchesManagerProps {
    tranches: Tranche[];
    setTranches: React.Dispatch<React.SetStateAction<Tranche[]>>;
    onChanged: () => void;
    validationError?: boolean;
}

export default function TranchesManager({
    tranches,
    setTranches,
    onChanged,
    validationError,
}: TranchesManagerProps) {
    const { t } = useTranslation();
    const amountInput = useDecimalInput(MAX_AMOUNT);
    const shippingInput = useDecimalInput(MAX_AMOUNT);

    const isDuplicateAmount =
        amountInput.display !== "" &&
        tranches.some((tr) => tr.MinCommande === amountInput.numeric);
    const isAddDisabled = amountInput.display === "" || isDuplicateAmount;

    const handleAddTranche = useCallback(() => {
        if (isAddDisabled) return;
        setTranches((prev) =>
            rebuildTranches([
                ...prev,
                {
                    MinCommande: amountInput.numeric,
                    MaxCommande: 0,
                    FraisLivraison: shippingInput.numeric,
                },
            ])
        );
        amountInput.reset();
        shippingInput.reset();
        onChanged();
    }, [
        isAddDisabled,
        amountInput,
        shippingInput,
        setTranches,
        onChanged,
    ]);

    const handleRemoveTranche = useCallback(
        (index: number) => {
            setTranches((prev) =>
                rebuildTranches(prev.filter((_, i) => i !== index))
            );
            amountInput.reset();
            shippingInput.reset();
            onChanged();
        },
        [amountInput, shippingInput, setTranches, onChanged]
    );

    return (
        <>
            <div
                className="d-flex align-items-end"
                style={{ gap: 12, marginBottom: 12 }}
            >
                <div className="tier-input-wrapper__clz">
                    <label>{t("Order amount")}</label>
                    <input
                        data-testid="tranche-amount-input"
                        className={classnames("form-control-input-zone__clz", {
                            input__clz: isDuplicateAmount,
                        })}
                        type="text"
                        placeholder={`${t("Enter amount")}`}
                        value={amountInput.display}
                        onChange={(e) => amountInput.onChange(e.target.value)}
                        onBlur={amountInput.onBlur}
                    />
                </div>
                <div className="tier-input-wrapper__clz">
                    <label>{t("Shipping costs")}</label>
                    <input
                        data-testid="tranche-shipping-input"
                        className="form-control-input-zone__clz"
                        type="text"
                        placeholder={`${t("Enter amount")}`}
                        value={shippingInput.display}
                        onChange={(e) => shippingInput.onChange(e.target.value)}
                        onBlur={shippingInput.onBlur}
                    />
                </div>
                <div
                    data-testid="tranche-add-btn"
                    className={classnames("tier-add-btn__clz", {
                        "tier-add-btn__clz--disabled": isAddDisabled,
                    })}
                    onClick={isAddDisabled ? undefined : handleAddTranche}
                >
                    <PlusIcon height={16} width={16} fill="#000" />
                </div>
            </div>
            {isDuplicateAmount && (
                <ErrorMessage message={t("This amount already exists")} />
            )}

            {tranches.length > 0 && (
                <>
                    <table
                        data-testid="tranche-table"
                        className="tier-table__clz"
                    >
                        <thead>
                            <tr>
                                <th>{t("Minimum amount")}</th>
                                <th>{t("Maximum amount")}</th>
                                <th>{t("Shipping costs")}</th>
                                <th style={{ textAlign: "center" }}>
                                    {t("Actions")}
                                </th>
                            </tr>
                        </thead>
                    </table>
                    <div style={{ maxHeight: "260px", overflowY: "auto" }}>
                        <table className="tier-table__clz">
                            <tbody>
                                {tranches.map((tr, idx) => (
                                    <tr key={tr.MinCommande}>
                                        <td>{Number(tr.MinCommande).toFixed(2)}</td>
                                        <td>
                                            {Number(tr.MaxCommande) === 0
                                                ? "∞"
                                                : Number(tr.MaxCommande).toFixed(2)}
                                        </td>
                                        <td>{Number(tr.FraisLivraison).toFixed(2)}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <TrashIcon
                                                data-testid={`tranche-delete-btn-${idx}`}
                                                className="tier-delete-btn__clz"
                                                height={15}
                                                width={15}
                                                fill="#e74c3c"
                                                onClick={() =>
                                                    handleRemoveTranche(idx)
                                                }
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {validationError && (
                <ErrorMessage message={t("Please add at least one fee tier")} />
            )}
        </>
    );
}
